import React, { useState } from "react";

interface PillSelectorProps {
  options: string[];
  selected: string[];
  onChange: (updated: string[]) => void;
  allowCustom?: boolean;
  "data-cy"?: string;
}

const PillSelector: React.FC<PillSelectorProps> = ({
  options,
  selected,
  onChange,
  allowCustom = false,
  "data-cy": dataCy,
 }) => {
  const [customInput, setCustomInput] = useState("");

  const toggle = (value: string) => {
    const updated = selected.includes(value)
      ? selected.filter((v) => v !== value)
      : [...selected, value];
    onChange(updated);
  };

  const handleCustomAdd = () => {
    const trimmedInput = customInput.trim();
    if (trimmedInput && !selected.includes(trimmedInput)) {
      onChange([...selected, trimmedInput]);
      setCustomInput("");
    }
  };

  const handleKeyPress = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      handleCustomAdd();
    }
  };

  // Combine predefined options with custom selections
  const customSelections = selected.filter(item => !options.includes(item));
  const allOptions = [...options, ...customSelections];

    // Helper function to conditionally add data-cy attribute
    const getDataCyProps = (suffix: string) =>
        dataCy ? { "data-cy": `${dataCy}-${suffix}` } : {};


    return (
    <div className="flex flex-wrap gap-2">
      {allOptions.map((opt) => (
        <button
          key={opt}
          type="button"
          onClick={() => toggle(opt)}
          aria-pressed={selected.includes(opt)}
          {...(dataCy && {
              "data-cy": `${dataCy}-option-${opt.toLowerCase().replace(/\s+/g, '-')}`
          })}
          className={`px-3 py-1.5 rounded-full border text-sm transition-colors h-8 ${
            selected.includes(opt)
              ? "bg-bolt text-white font-semibold border-bolt"
              : "bg-transparent border-gray-400 text-gray-500 hover:border-gray-600"
          }`}
        >
          {opt}
        </button>
      ))}
      {allowCustom && (
        <div className="flex gap-1 items-center mt-2">
          <input
            type="text"
            value={customInput}
            onChange={(e) => setCustomInput(e.target.value)}
            onKeyPress={handleKeyPress}
            placeholder="Add custom"
            {...getDataCyProps("custom-input")}
            className="px-3 py-2 text-sm bg-redwoodInputFieldBg border border-[#334155] text-white rounded focus:outline-none focus:ring-2 focus:ring-ring focus:border-ring"
          />
          <button
            type="button"
            onClick={handleCustomAdd}
            {...getDataCyProps("custom-add")}
            className="px-3 py-2 bg-bolt text-white rounded text-sm hover:bg-[#158670] transition-colors font-medium"
          >
            +
          </button>
        </div>
      )}
    </div>
  );
};

export default PillSelector;